import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { IOrderById, IOrderData } from '../../models/order.models';
import { EScenarioType } from '../../configurations/order-status';
import { environment } from '../../../environments/environment';
import { OrdersFacade } from '../../facades/orders.facade';
import { EFeatureToggles, EMultiCartTabs, ESparePartsOrderHistoryTabs } from '../../configurations/common';
import { IconUtils } from '../../utils/icon.utils';
import { TranslateService } from '@ngx-translate/core';
import { ConfigurationFacade } from '../../facades/configuration.facade';
import { ImageUtils } from '../../utils/image.utils';
import { ImagePreset } from '../../models/image.models';

@Component({
  selector: 'app-spare-part-order-details',
  templateUrl: './spare-part-order-details.component.html',
  styleUrls: ['./spare-part-order-details.component.scss'],
  standalone: false,
})
export class SparePartOrderDetailsComponent implements OnInit, OnDestroy {
  private route: ActivatedRoute = inject(ActivatedRoute);
  private router: Router = inject(Router);
  private ordersFacade: OrdersFacade = inject(OrdersFacade);
  private translateService: TranslateService = inject(TranslateService);
  private configurationFacade: ConfigurationFacade = inject(ConfigurationFacade);

  orderDetails!: IOrderData;
  orderDetailsAccordion: boolean = false;
  isLoaded: boolean = false;

  scenarioType: EScenarioType = EScenarioType.SPARE_PART_ORDER_DETAILS;
  returnDocumentUrl: string = '';
  sparePartsReturnDocumentUrlKey: string = environment.sparePartsReturnDocumentUrlKey;
  isReturnableDocumentEmailLinkEnable: boolean = false;
  isProductDiscontinuedStatusEnabled$: Observable<boolean> = new Observable<boolean>();

  private readonly unsubscribe$: Subject<void> = new Subject<void>();
  protected readonly ImageUtils: typeof ImageUtils = ImageUtils;
  protected readonly ImagePreset: typeof ImagePreset = ImagePreset;

  ngOnInit(): void {
    this.isProductDiscontinuedStatusEnabled$ = this.configurationFacade.isFeatureEnabled(EFeatureToggles.PRODUCT_DISCONTINUED_STATUS);
    this.route.params
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(params => {
        this.getOrderDetails(params.orderId);
      });

    this.configurationFacade.isFeatureEnabled(EFeatureToggles.SUBMIT_RETURN_DOCUMENT).pipe(
      takeUntil(this.unsubscribe$),
    ).subscribe(value => {
      this.isReturnableDocumentEmailLinkEnable = value;
    });

    this._getReturnDocumentUrl();
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  getOrderDetails(orderId: string): void {
    this.ordersFacade.getOrderById(orderId).pipe(
      takeUntil(this.unsubscribe$),
    ).subscribe((order: IOrderById) => {
      this.orderDetails = order.data;
      this.isLoaded = true;
    });
  }

  getIcon(): string {
    return IconUtils.getStatusIcon(this.orderDetails.attributes.stateDisplayName ?? '');
  }

  getStatus(status: string | undefined): string {
    if (!status || status === '') {
      return '';
    }

    return status.replace(' ', '-');
  }

  getTrackingNumbers(item: any): string[] {
    if (item.sapDetails?.trackingNumber) {
      return item.sapDetails.trackingNumber.split(',');
    }

    return [];
  }

  getTotalItemTranslation(itemsNumber: number): string {
    return itemsNumber <= 1
      ? 'order-thank-you-page.total-item-number-singular'
      : 'order-thank-you-page.total-item-number-plural';
  }

  toggleOrderDetailsAccordion(): void {
    this.orderDetailsAccordion = !this.orderDetailsAccordion;
  }

  downloadDocumentation(): void {
    window.open(this.returnDocumentUrl, '_blank');
  }

  redirectBackToPurchaseActivity(orderId: string): void {
    this.router.navigate(
      ['/purchase-activity'],
      {
        queryParams: {
          tab: EMultiCartTabs.PARTS,
          subtab: ESparePartsOrderHistoryTabs.ORDERS,
          orderId: orderId,
        },
      },
    );
  }

  private _getReturnDocumentUrl(): void {
    this.translateService.get(this.sparePartsReturnDocumentUrlKey).pipe(
      takeUntil(this.unsubscribe$),
    ).subscribe(value => {
      this.returnDocumentUrl = value;
    });
  }
}
